import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  Section,
  Select,
  Spinner,
  Switch,
  Textarea,
} from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { AttributeDef, ProductTag } from '../lib/types';

/**
 * Ürün özellikleri (F5B-01): işletme düzeyinde özellik tanımları (ör. "Net ağırlık" sayı + kg, "Yaş grubu"
 * seçenek) ve kullanılan etiketler. Değerler ürün ekranında girilir.
 */

export const ATTRIBUTE_TYPES = ['text', 'number', 'select', 'multiselect', 'boolean'] as const;
type AttrType = (typeof ATTRIBUTE_TYPES)[number];

export function useAttributeDefs() {
  return useQuery({
    queryKey: ['product-attributes'],
    queryFn: () => api.get<AttributeDef[]>('/v1/admin/product-attributes'),
  });
}

export function AttributesPage() {
  const t = useT();
  const { formatNumber } = useI18n();
  const { can } = usePermissions();
  const canWrite = can('catalog.product.write');
  const q = useAttributeDefs();
  const tags = useQuery({
    queryKey: ['product-tags'],
    queryFn: () => api.get<ProductTag[]>('/v1/admin/product-tags'),
  });
  const [editing, setEditing] = useState<AttributeDef | 'new' | null>(null);
  const del = useApiMutation((id: string) => api.del(`/v1/admin/product-attributes/${id}`), {
    invalidate: [['product-attributes'], ['product']],
    success: t('admin.attributes.deleted'),
  });
  return (
    <PageFrame helpKey="page.attributes">
      <Section
        title={t('admin.attributes.title')}
        description={t('admin.attributes.lead')}
        action={
          <span className="flex items-center gap-1">
            {canWrite && (
              <Button size="sm" variant="primary" onClick={() => setEditing('new')}>
                {t('admin.attributes.add')}
              </Button>
            )}
            <HelpTip k="attributes.list" />
          </span>
        }
      >
        {!q.data ? (
          <Spinner label={t('common.loading')} />
        ) : q.data.length === 0 ? (
          <EmptyState
            title={t('admin.attributes.emptyTitle')}
            description={t('admin.attributes.emptyLead')}
          />
        ) : (
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full text-sm" aria-label={t('admin.attributes.title')}>
              <thead className="bg-surface-2 text-ink-2">
                <tr>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.attributes.name')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.attributes.type')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.attributes.options')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.attributes.where')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.attributes.productCount')}</th>
                  <th className="px-3 py-2 text-end font-medium">
                    <span className="sr-only">{t('common.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {q.data.map((a) => (
                  <tr key={a.id} className="border-t border-line align-top">
                    <td className="px-3 py-2">
                      <span className="font-medium">{a.name}</span>
                      <span className="block font-mono text-xs text-ink-3">{a.code}</span>
                    </td>
                    <td className="px-3 py-2">
                      {t(`admin.attributes.types.${a.type}`)}
                      {a.unit && <span className="text-ink-3"> ({a.unit})</span>}
                    </td>
                    <td className="px-3 py-2 text-ink-2">{a.options.length ? a.options.join(', ') : '—'}</td>
                    <td className="px-3 py-2">
                      <span className="flex flex-wrap gap-1">
                        {a.visible && <Badge tone="moss">{t('admin.attributes.onProductPage')}</Badge>}
                        {a.filterable && <Badge tone="pine">{t('admin.attributes.inFilters')}</Badge>}
                        {!a.visible && !a.filterable && (
                          <Badge tone="slate">{t('admin.attributes.internal')}</Badge>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-end tabular-nums">{formatNumber(a.productCount)}</td>
                    <td className="px-3 py-2 text-end whitespace-nowrap">
                      {canWrite && (
                        <span className="inline-flex gap-1">
                          <Button
                            size="sm"
                            onClick={() => setEditing(a)}
                            aria-label={t('admin.attributes.editItem', { name: a.name })}
                          >
                            {t('common.edit')}
                          </Button>
                          <ConfirmButton
                            variant="ghost"
                            message={t('admin.attributes.deleteConfirm', {
                              name: a.name,
                              count: a.productCount,
                            })}
                            onConfirm={() => del.mutateAsync(a.id)}
                          >
                            {t('common.delete')}
                          </ConfirmButton>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
      <Section
        title={t('admin.attributes.tagsTitle')}
        description={t('admin.attributes.tagsLead')}
        action={<HelpTip k="products.tags" />}
      >
        {!tags.data ? (
          <Spinner label={t('common.loading')} />
        ) : tags.data.length === 0 ? (
          <p className="text-sm text-ink-3">{t('admin.attributes.noTags')}</p>
        ) : (
          <ul className="flex flex-wrap gap-2" aria-label={t('admin.attributes.tagsTitle')}>
            {tags.data.map((tg) => (
              <li key={tg.tag}>
                <Badge tone="neutral">
                  {tg.tag} · {formatNumber(tg.productCount)}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </Section>
      {editing && (
        <AttributeDialog row={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
    </PageFrame>
  );
}

function AttributeDialog({ row, onClose }: { row: AttributeDef | null; onClose: () => void }) {
  const t = useT();
  const [f, setF] = useState({
    name: row?.name ?? '',
    code: row?.code ?? '',
    type: (row?.type ?? 'text') as AttrType,
    unit: row?.unit ?? '',
    options: (row?.options ?? []).join('\n'),
    filterable: row?.filterable ?? false,
    visible: row?.visible ?? true,
    position: String(row?.position ?? 0),
  });
  const choice = f.type === 'select' || f.type === 'multiselect';
  const body = {
    name: f.name,
    ...(f.code.trim() ? { code: f.code.trim() } : {}),
    type: f.type,
    unit: f.type === 'number' ? f.unit.trim() || null : null,
    options: choice
      ? f.options
          .split('\n')
          .map((o) => o.trim())
          .filter(Boolean)
      : [],
    filterable: f.filterable,
    visible: f.visible,
    position: Number(f.position) || 0,
  };
  const save = useApiMutation(
    () =>
      row
        ? api.patch(`/v1/admin/product-attributes/${row.id}`, { ...body, version: row.version })
        : api.post('/v1/admin/product-attributes', body),
    {
      invalidate: [['product-attributes']],
      success: t('admin.attributes.saved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={row ? t('admin.attributes.edit') : t('admin.attributes.add')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('admin.attributes.name')} required error={fieldError(save.error, 'name')}>
          {(p) => (
            <Input
              {...p}
              value={f.name}
              maxLength={60}
              onChange={(e) => setF({ ...f, name: e.target.value })}
            />
          )}
        </Field>
        <Field
          label={t('admin.attributes.code')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="attributes.code" />}
          error={fieldError(save.error, 'code')}
        >
          {(p) => (
            <Input
              {...p}
              value={f.code}
              maxLength={40}
              placeholder="net-agirlik"
              onChange={(e) => setF({ ...f, code: e.target.value })}
            />
          )}
        </Field>
        <Field label={t('admin.attributes.type')} help={<HelpTip k="attributes.type" />}>
          {(p) => (
            <Select {...p} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value as AttrType })}>
              {ATTRIBUTE_TYPES.map((x) => (
                <option key={x} value={x}>
                  {t(`admin.attributes.types.${x}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {f.type === 'number' && (
          <Field label={t('admin.attributes.unit')} optionalLabel={t('common.optional')}>
            {(p) => (
              <Input
                {...p}
                value={f.unit}
                maxLength={10}
                placeholder="kg"
                onChange={(e) => setF({ ...f, unit: e.target.value })}
              />
            )}
          </Field>
        )}
        {choice && (
          <Field
            label={t('admin.attributes.options')}
            required
            help={<HelpTip k="attributes.options" />}
            error={fieldError(save.error, 'options')}
          >
            {(p) => (
              <Textarea
                {...p}
                rows={5}
                value={f.options}
                placeholder={t('admin.attributes.optionsPlaceholder')}
                onChange={(e) => setF({ ...f, options: e.target.value })}
              />
            )}
          </Field>
        )}
        <div className="flex items-center justify-between gap-3">
          <span className="flex items-center gap-1 text-sm">
            {t('admin.attributes.visible')} <HelpTip k="attributes.visible" />
          </span>
          <Switch
            checked={f.visible}
            onCheckedChange={(v) => setF({ ...f, visible: v })}
            label={t('admin.attributes.visible')}
          />
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="flex items-center gap-1 text-sm">
            {t('admin.attributes.filterable')} <HelpTip k="attributes.filterable" />
          </span>
          <Switch
            checked={f.filterable}
            onCheckedChange={(v) => setF({ ...f, filterable: v })}
            label={t('admin.attributes.filterable')}
          />
        </div>
        <Field label={t('admin.attributes.position')} help={<HelpTip k="attributes.position" />}>
          {(p) => (
            <Input
              {...p}
              type="number"
              min={0}
              max={10000}
              value={f.position}
              onChange={(e) => setF({ ...f, position: e.target.value })}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
